import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Trophy, Star, Zap, Flame, Crown, Target, TrendingUp, 
  Gift, Award, Clock, ChevronRight, Lock, Check, 
  Sparkles, Users, Calendar, Info, ArrowUp, AlertTriangle,
  ArrowRightLeft, Percent, Timer, Shield, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useApp } from "../contexts/AppContext";
import { 
  PRESTIGE_TIERS, getPrestigeTier, getLevelFromXP, getXPForLevel, getTotalXPForLevel,
  EARNING_RULES, BADGES, BADGE_RARITY, BADGE_CATEGORIES,
  CURRENT_SEASON, DEMO_LEADERBOARD, DEMO_BURN_STATS,
  calculateXPMultiplier, calculateConversion, CONVERSION_CONFIG, LEVEL_CONFIG,
  DUMP_SAFEGUARDS, BURN_EVENTS, ECONOMY_PHILOSOPHY
} from "../constants/rewards";
import { userAPI, rewardsAPI } from "../services/api";

export const RewardsPage = () => {
  const { user } = useApp();
  const [activeTab, setActiveTab] = useState("overview");
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertAmount, setConvertAmount] = useState(10000);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [conversionSuccess, setConversionSuccess] = useState(null);
  
  // Referral rewards state
  const [referralProgress, setReferralProgress] = useState(null);
  const [referralTiers, setReferralTiers] = useState(null);
  const [referralLeaderboard, setReferralLeaderboard] = useState([]);
  const [claimingTier, setClaimingTier] = useState(null);
  const [claimResult, setClaimResult] = useState(null);
  
  // User data state (fetched from API or fallback to demo)
  const [userData, setUserData] = useState({
    totalXP: user?.xp || 32847,
    level: user?.level || 28,
    burnzTokens: user?.burnz_tokens || 450,
    streakDays: user?.streak_days || 12,
    questsCompleted: 67,
    nftsDumped: 14,
    poolsJoined: 6,
    seasonalXP: 8250,
    rank: 42,
    earnedBadges: ['first_flame', 'quest_master', 'letting_go', 'community_builder', 'level_10', 'level_25', 'early_adopter', 'first_burn'],
    totalBurnzBurned: 4500,
    lastConversion: '2026-01-15',
    dumpsToday: 2,
  });

  // Fetch user overview from API
  useEffect(() => {
    const fetchUserOverview = async () => {
      setLoading(true);
      try {
        const data = await userAPI.getOverview();
        if (data) {
          setUserData(prev => ({
            ...prev,
            totalXP: data.total_xp || prev.totalXP,
            level: data.level || prev.level,
            burnzTokens: data.burnz_balance || prev.burnzTokens,
            streakDays: data.streak_days || prev.streakDays,
            questsCompleted: data.quests_completed || prev.questsCompleted,
            nftsDumped: data.nfts_dumped || prev.nftsDumped,
            seasonalXP: data.seasonal_xp || prev.seasonalXP,
            rank: data.rank || prev.rank,
            earnedBadges: data.badges || prev.earnedBadges,
            totalBurnzBurned: data.total_burnz_burned || prev.totalBurnzBurned,
          }));
        }
      } catch (err) {
        // Keep demo data on error
      }
      setLoading(false);
    };

    fetchUserOverview();
  }, []);

  // Fetch referral rewards data
  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        const [tiersData, progressData, leaderboardData] = await Promise.all([
          rewardsAPI.getTiers(),
          rewardsAPI.getMyProgress(),
          rewardsAPI.getLeaderboard(10)
        ]);
        
        if (tiersData?.tiers) setReferralTiers(tiersData.tiers);
        if (progressData && !progressData.error) setReferralProgress(progressData);
        if (leaderboardData?.leaderboard) setReferralLeaderboard(leaderboardData.leaderboard);
      } catch (err) {
        console.error("Error fetching referral data:", err);
      }
    };

    fetchReferralData();
  }, []);

  // Handle claiming referral tier reward
  const handleClaimTierReward = async (tier) => {
    setClaimingTier(tier);
    setClaimResult(null);
    try {
      const result = await rewardsAPI.claimTierReward(tier);
      setClaimResult({ success: true, ...result });
      // Refresh progress
      const progressData = await rewardsAPI.getMyProgress();
      if (progressData && !progressData.error) setReferralProgress(progressData);
    } catch (err) {
      setClaimResult({ success: false, message: err.message });
    }
    setClaimingTier(null);
  };

  // Handle XP conversion
  const handleConvertXP = async () => {
    setConverting(true);
    setConversionSuccess(null);
    
    try {
      const result = await userAPI.convertXP(convertAmount);
      
      // Update local state
      setUserData(prev => ({
        ...prev,
        totalXP: prev.totalXP - convertAmount,
        burnzTokens: prev.burnzTokens + (result.burnz_earned || conversionInfo.burnzEarned) - (result.burnz_burned || conversionInfo.burnzCost),
        totalBurnzBurned: prev.totalBurnzBurned + (result.burnz_burned || conversionInfo.burnzCost),
      }));
      
      setConversionSuccess(true);
      setTimeout(() => {
        setShowConvertModal(false);
        setConversionSuccess(null);
      }, 1500);
    } catch (err) {
      setConversionSuccess(false);
    }
    
    setConverting(false);
  };

  const currentTier = getPrestigeTier(userData.level);
  const nextTier = PRESTIGE_TIERS.find(t => t.minLevel > userData.level);
  const xpForCurrentLevel = getXPForLevel(userData.level + 1);
  const xpIntoLevel = userData.totalXP - getTotalXPForLevel(userData.level);
  const levelProgress = (xpIntoLevel / xpForCurrentLevel) * 100;
  
  const xpMultiplier = calculateXPMultiplier(userData.level, userData.streakDays, false);
  const conversionInfo = calculateConversion(convertAmount, currentTier.id);

  return (
    <div className="space-y-6">
      {/* XP Conversion Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowConvertModal(false)}>
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#1A1A1A] border border-orange-500/30 rounded-2xl p-6 max-w-md w-full mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <ArrowRightLeft className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Convert XP to BURNZ</h3>
                <p className="text-white/50 text-sm">Tier: {currentTier.name} ({CONVERSION_CONFIG.tierBonuses[currentTier.id]}x rate)</p>
              </div>
            </div>

            {!currentTier.canConvertXP ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 text-red-400 mb-2">
                  <Lock className="w-5 h-5" />
                  <span className="font-bold">Conversion Locked</span>
                </div>
                <p className="text-white/70 text-sm">You need to reach <span className="text-cyan-400 font-bold">Silver tier (Level 25)</span> to unlock XP conversion.</p>
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-white/50 text-sm mb-2 block">XP to Convert</label>
                    <input 
                      type="range" 
                      min="10000" 
                      max={Math.min(userData.totalXP, 100000)} 
                      step="1000"
                      value={convertAmount}
                      onChange={(e) => setConvertAmount(Number(e.target.value))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-white/50">10,000 XP</span>
                      <span className="text-orange-400 font-bold">{convertAmount.toLocaleString()} XP</span>
                      <span className="text-white/50">{Math.min(userData.totalXP, 100000).toLocaleString()} XP</span>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/50">You will receive:</span>
                      <span className="text-green-400 font-bold text-lg">+{conversionInfo.burnzEarned} BURNZ</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">Burn cost (supply reduction):</span>
                      <span className="text-red-400 font-bold">-{conversionInfo.burnzCost} BURNZ</span>
                    </div>
                    <div className="border-t border-white/10 pt-3 flex justify-between">
                      <span className="text-white/70">Net BURNZ gained:</span>
                      <span className="text-orange-400 font-bold text-lg">{conversionInfo.burnzEarned - conversionInfo.burnzCost} BURNZ</span>
                    </div>
                  </div>

                  {userData.burnzTokens < conversionInfo.burnzCost && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                      <div className="flex items-center gap-2 text-yellow-400 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        <span>You need {conversionInfo.burnzCost} BURNZ to cover the burn cost. Current balance: {userData.burnzTokens}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-3 mb-4">
                  <p className="text-orange-300 text-sm flex items-center gap-2">
                    <Flame className="w-4 h-4" />
                    <span>This burn permanently reduces BURNZ supply, making tokens more valuable!</span>
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={() => setShowConvertModal(false)}>Cancel</Button>
                  <Button 
                    className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                    disabled={userData.burnzTokens < conversionInfo.burnzCost || !conversionInfo.eligible || converting}
                    onClick={handleConvertXP}
                  >
                    {converting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Converting...
                      </>
                    ) : conversionSuccess === true ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Success!
                      </>
                    ) : conversionSuccess === false ? (
                      <>
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Failed
                      </>
                    ) : (
                      <>
                        <Flame className="w-4 h-4 mr-2" />
                        Convert & Burn
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-3xl font-bold text-white flex items-center gap-2 lg:gap-3">
            <Trophy className="w-6 h-6 lg:w-8 lg:h-8 text-yellow-400" />
            Rewards
          </h1>
          <p className="text-white/50 text-xs lg:text-base mt-0.5 lg:mt-1">Earn XP, convert strategically, burn to earn</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-white/50 text-[10px] lg:text-xs">Your Rank</p>
            <p className="text-lg lg:text-2xl font-bold text-white">#{userData.rank}</p>
          </div>
          <div className={`w-10 h-10 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-gradient-to-br ${currentTier.gradient} flex items-center justify-center text-lg lg:text-2xl shadow-lg`}>
            {currentTier.icon}
          </div>
        </div>
      </div>

      {/* Tabs - Scrollable on mobile */}
      <div className="flex items-center gap-1 lg:gap-2 border-b border-white/10 pb-1 overflow-x-auto scrollbar-hide">
        {[
          { id: "overview", label: "Overview", icon: Target },
          { id: "referral-rewards", label: "Referral", icon: Users },
          { id: "convert", label: "XP → BURNZ", icon: ArrowRightLeft },
          { id: "burns", label: "Burns", icon: Flame },
          { id: "progression", label: "Tiers", icon: TrendingUp },
          { id: "badges", label: "Badges", icon: Award },
          { id: "seasonal", label: "Season", icon: Calendar },
          { id: "leaderboard", label: "Leaders", icon: Crown },
          { id: "earning", label: "Earn", icon: Info },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm font-medium rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === tab.id 
                ? "text-purple-400 bg-purple-500/10 border-b-2 border-purple-500" 
                : "text-white/50 hover:text-white"
            }`}
          >
            <tab.icon className="w-3.5 h-3.5 lg:w-4 lg:h-4" /><span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-4 lg:space-y-6">
          {/* Economy Philosophy Banner */}
          <Card className="bg-gradient-to-r from-orange-500/10 via-purple-500/10 to-cyan-500/10 border-orange-500/20">
            <CardContent className="p-3 lg:p-4">
              <p className="text-white/80 text-center text-xs lg:text-sm">
                <Sparkles className="w-3.5 h-3.5 lg:w-4 lg:h-4 inline mr-1 lg:mr-2 text-orange-400" />
                {ECONOMY_PHILOSOPHY.mission}
              </p>
            </CardContent>
          </Card>

          {/* Level Progress Card */}
          <Card className="bg-gradient-to-br from-purple-900/50 via-[#1A1A1A] to-orange-900/30 border-white/10 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                {/* Level Badge */}
                <div className="relative">
                  <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${currentTier.gradient} flex items-center justify-center shadow-2xl`}>
                    <div className="text-center">
                      <span className="text-3xl">{currentTier.icon}</span>
                      <p className="text-white font-bold text-lg">Lv.{userData.level}</p>
                    </div>
                  </div>
                  {currentTier.canConvertXP && (
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-[#1A1A1A]">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>

                {/* Progress Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white font-bold text-xl">{currentTier.name}</span>
                    {nextTier && (
                      <>
                        <ChevronRight className="w-4 h-4 text-white/30" />
                        <span className="text-white/30">{nextTier.name} (Lv.{nextTier.minLevel})</span>
                      </>
                    )}
                  </div>
                  
                  {/* XP Bar */}
                  <div className="mb-2">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/50">Level {userData.level} → {userData.level + 1}</span>
                      <span className="text-orange-400 font-bold">{xpIntoLevel.toLocaleString()} / {xpForCurrentLevel.toLocaleString()} XP</span>
                    </div>
                    <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${levelProgress}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="h-full bg-gradient-to-r from-orange-500 via-purple-500 to-pink-500 rounded-full relative"
                      >
                        <div className="absolute inset-0 bg-white/20 animate-pulse" style={{ animationDuration: '2s' }} />
                      </motion.div>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <span className="text-white/50">Total XP:</span>
                      <span className="text-white font-bold ml-2">{userData.totalXP.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-white/50">XP Multiplier:</span>
                      <span className="text-green-400 font-bold ml-2">{xpMultiplier.toFixed(2)}x</span>
                    </div>
                    <div>
                      <span className="text-white/50">Streak:</span>
                      <span className="text-orange-400 font-bold ml-2">{userData.streakDays} days 🔥</span>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white/5 rounded-xl text-center">
                    <p className="text-2xl font-bold text-orange-400">{userData.burnzTokens.toLocaleString()}</p>
                    <p className="text-white/50 text-xs">BURNZ</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-xl text-center">
                    <p className="text-2xl font-bold text-red-400">{userData.totalBurnzBurned.toLocaleString()}</p>
                    <p className="text-white/50 text-xs">Burned 🔥</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions & Stats */}
          <div className="grid grid-cols-4 gap-4">
            {/* Convert Button */}
            <Card className={`border cursor-pointer transition-all hover:scale-105 ${currentTier.canConvertXP ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20 border-orange-500/30' : 'bg-[#1A1A1A] border-white/10 opacity-60'}`}
              onClick={() => currentTier.canConvertXP && setShowConvertModal(true)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-xl ${currentTier.canConvertXP ? 'bg-orange-500/20' : 'bg-white/10'} flex items-center justify-center`}>
                    {currentTier.canConvertXP ? <ArrowRightLeft className="w-5 h-5 text-orange-400" /> : <Lock className="w-5 h-5 text-white/30" />}
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">XP → BURNZ</p>
                    <p className={`font-bold ${currentTier.canConvertXP ? 'text-orange-400' : 'text-white/30'}`}>
                      {currentTier.canConvertXP ? 'Convert Now' : 'Lv.25 Required'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1A1A1A] border-green-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">XP Multiplier</p>
                    <p className="text-green-400 font-bold text-xl">{xpMultiplier.toFixed(2)}x</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1A1A1A] border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Voting Power</p>
                    <p className="text-purple-400 font-bold text-xl">{currentTier.votingPower}x</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1A1A1A] border-cyan-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Season XP</p>
                    <p className="text-cyan-400 font-bold text-xl">{userData.seasonalXP.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Economy Layers */}
          <Card className="bg-[#1A1A1A] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-400" />
                Economy Layers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(ECONOMY_PHILOSOPHY.layers).map(([key, layer]) => (
                  <div key={key} className="p-4 bg-white/5 rounded-xl text-center">
                    <p className="text-white/50 text-xs uppercase mb-2">{key}</p>
                    <p className="text-white font-bold text-lg mb-1">{layer.asset}</p>
                    <p className="text-white/50 text-xs">{layer.purpose}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Referral Rewards Tab */}
      {activeTab === "referral-rewards" && (
        <div className="space-y-6">
          {/* Referral Stats Banner */}
          <Card className="bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 border-cyan-500/20">
            <CardContent className="p-4 lg:p-6">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Your Referrals</p>
                    <p className="text-4xl font-bold text-white">{referralProgress?.referral_count || 0}</p>
                  </div>
                </div>
                <div className="text-center lg:text-right">
                  <p className="text-white/50 text-sm">Current Tier</p>
                  <p className="text-2xl font-bold text-cyan-400 capitalize">{referralProgress?.current_tier || 'None'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Claim Result Toast */}
          {claimResult && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-xl border ${claimResult.success ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
            >
              <div className="flex items-center gap-3">
                {claimResult.success ? (
                  <>
                    <Check className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-green-400 font-bold">{claimResult.message}</p>
                      <p className="text-white/70 text-sm">+{claimResult.xp_awarded?.toLocaleString()} XP, +{claimResult.burnz_awarded?.toLocaleString()} BURNZ</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <p className="text-red-400">{claimResult.message}</p>
                  </>
                )}
              </div>
            </motion.div>
          )}

          {/* Reward Tiers Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {referralTiers && Object.entries(referralTiers).map(([tierName, tierData]) => {
              const progress = referralProgress?.progress?.[tierName] || {};
              const isUnlocked = progress.unlocked;
              const isClaimed = progress.claimed;
              const progressPercent = progress.progress_percent || 0;
              
              const tierColors = {
                bronze: { bg: 'from-amber-700/20 to-amber-900/20', border: 'border-amber-500/30', text: 'text-amber-400', icon: '🥉' },
                silver: { bg: 'from-gray-400/20 to-gray-600/20', border: 'border-gray-400/30', text: 'text-gray-300', icon: '🥈' },
                gold: { bg: 'from-yellow-500/20 to-yellow-700/20', border: 'border-yellow-500/30', text: 'text-yellow-400', icon: '🥇' },
                platinum: { bg: 'from-cyan-500/20 to-cyan-700/20', border: 'border-cyan-500/30', text: 'text-cyan-400', icon: '💎' },
                diamond: { bg: 'from-purple-500/20 to-purple-700/20', border: 'border-purple-500/30', text: 'text-purple-400', icon: '👑' }
              };
              const colors = tierColors[tierName] || tierColors.bronze;
              
              return (
                <Card key={tierName} className={`bg-gradient-to-br ${colors.bg} ${colors.border} relative overflow-hidden`}>
                  {isClaimed && (
                    <div className="absolute top-2 right-2 bg-green-500/20 border border-green-500/30 rounded-full px-2 py-1">
                      <span className="text-green-400 text-xs font-bold flex items-center gap-1">
                        <Check className="w-3 h-3" /> Claimed
                      </span>
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl">{colors.icon}</span>
                      <div>
                        <h3 className={`font-bold text-lg capitalize ${colors.text}`}>{tierName}</h3>
                        <p className="text-white/50 text-sm">{tierData.min_referrals} referrals</p>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white/50">Progress</span>
                        <span className={colors.text}>{Math.round(progressPercent)}%</span>
                      </div>
                      <Progress value={progressPercent} className="h-2" />
                      <p className="text-white/40 text-xs mt-1">
                        {referralProgress?.referral_count || 0} / {tierData.min_referrals} referrals
                      </p>
                    </div>
                    
                    {/* Rewards */}
                    <div className="bg-black/30 rounded-lg p-3 space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">XP Bonus</span>
                        <span className="text-green-400 font-bold">+{tierData.xp_bonus?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">BURNZ Bonus</span>
                        <span className="text-orange-400 font-bold">+{tierData.burnz_bonus?.toLocaleString()}</span>
                      </div>
                    </div>
                    
                    {/* Claim Button */}
                    <Button
                      className={`w-full ${isUnlocked && !isClaimed ? 'bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600' : ''}`}
                      disabled={!isUnlocked || isClaimed || claimingTier === tierName}
                      onClick={() => handleClaimTierReward(tierName)}
                    >
                      {claimingTier === tierName ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : isClaimed ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : !isUnlocked ? (
                        <Lock className="w-4 h-4 mr-2" />
                      ) : (
                        <Gift className="w-4 h-4 mr-2" />
                      )}
                      {isClaimed ? 'Claimed' : isUnlocked ? 'Claim Reward' : 'Locked'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Referral Rewards Leaderboard */}
          {referralLeaderboard.length > 0 && (
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-400" />
                  Top Reward Earners
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {referralLeaderboard.map((entry, index) => (
                    <div key={entry.user_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                          index === 1 ? 'bg-gray-400/20 text-gray-300' :
                          index === 2 ? 'bg-amber-600/20 text-amber-400' :
                          'bg-white/10 text-white/50'
                        }`}>
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-white/50 text-sm">User #{entry.user_id?.slice(-6)}</p>
                          <p className="text-cyan-400 font-bold capitalize">{entry.highest_tier || 'Bronze'} Tier</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold">+{entry.total_xp_earned?.toLocaleString()} XP</p>
                        <p className="text-orange-400 text-sm">+{entry.total_burnz_earned?.toLocaleString()} BURNZ</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* How It Works */}
          <Card className="bg-[#1A1A1A] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-400" />
                How Referral Rewards Work
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h4 className="text-white font-bold mb-1">1. Invite Friends</h4>
                  <p className="text-white/50 text-sm">Share your referral link from the Referrals page</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                    <Target className="w-6 h-6 text-purple-400" />
                  </div>
                  <h4 className="text-white font-bold mb-1">2. Reach Milestones</h4>
                  <p className="text-white/50 text-sm">Unlock tiers as your referral count grows</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                    <Gift className="w-6 h-6 text-green-400" />
                  </div>
                  <h4 className="text-white font-bold mb-1">3. Claim Rewards</h4>
                  <p className="text-white/50 text-sm">Get XP and BURNZ bonuses for each tier</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* XP to BURNZ Conversion Tab */}
      {activeTab === "convert" && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-6">
            {/* Conversion Card */}
            <div className="col-span-2">
              <Card className="bg-gradient-to-br from-orange-900/30 via-[#1A1A1A] to-red-900/30 border-orange-500/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5 text-orange-400" />
                    XP → BURNZ Conversion
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {!currentTier.canConvertXP ? (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
                      <Lock className="w-12 h-12 text-red-400 mx-auto mb-3" />
                      <h3 className="text-white font-bold text-lg mb-2">Conversion Locked</h3>
                      <p className="text-white/70 mb-4">Reach <span className="text-cyan-400 font-bold">Silver tier (Level 25)</span> to unlock XP conversion</p>
                      <div className="flex justify-center gap-2">
                        <Badge className="bg-white/10 text-white/50">Current: {currentTier.name}</Badge>
                        <Badge className="bg-cyan-500/20 text-cyan-400">Target: Silver (Lv.25)</Badge>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 bg-white/5 rounded-xl text-center">
                          <p className="text-white/50 text-xs mb-1">Your Available XP</p>
                          <p className="text-2xl font-bold text-orange-400">{userData.totalXP.toLocaleString()}</p>
                        </div>
                        <div className="p-4 bg-white/5 rounded-xl text-center">
                          <p className="text-white/50 text-xs mb-1">Conversion Rate</p>
                          <p className="text-2xl font-bold text-green-400">{CONVERSION_CONFIG.tierBonuses[currentTier.id]}x</p>
                        </div>
                        <div className="p-4 bg-white/5 rounded-xl text-center">
                          <p className="text-white/50 text-xs mb-1">Burn Cost</p>
                          <p className="text-2xl font-bold text-red-400">{(CONVERSION_CONFIG.tierBurnReduction[currentTier.id] * 100).toFixed(0)}%</p>
                        </div>
                      </div>

                      <div className="bg-white/5 rounded-xl p-4">
                        <label className="text-white/70 text-sm mb-3 block">Select XP amount to convert:</label>
                        <input 
                          type="range" 
                          min="10000" 
                          max={Math.min(userData.totalXP, 100000)} 
                          step="1000"
                          value={convertAmount}
                          onChange={(e) => setConvertAmount(Number(e.target.value))}
                          className="w-full mb-4"
                        />
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-white/50 text-xs">XP Spent</p>
                            <p className="text-orange-400 font-bold">{convertAmount.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs">BURNZ Received</p>
                            <p className="text-green-400 font-bold">+{conversionInfo.burnzEarned}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs">BURNZ Burned</p>
                            <p className="text-red-400 font-bold">-{conversionInfo.burnzCost}</p>
                          </div>
                        </div>
                      </div>

                      <Button 
                        className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                        onClick={() => setShowConvertModal(true)}
                      >
                        <Flame className="w-5 h-5 mr-2" />
                        Convert {convertAmount.toLocaleString()} XP → {conversionInfo.burnzEarned - conversionInfo.burnzCost} Net BURNZ
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Rules Card */}
            <Card className="bg-[#1A1A1A] border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-lg">Conversion Rules</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <Timer className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-white text-sm font-medium">Cooldown</p>
                      <p className="text-white/50 text-xs">{CONVERSION_CONFIG.cooldownDays} days between conversions</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <Target className="w-5 h-5 text-orange-400" />
                    <div>
                      <p className="text-white text-sm font-medium">Minimum XP</p>
                      <p className="text-white/50 text-xs">{CONVERSION_CONFIG.minXPToConvert.toLocaleString()} XP required</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <Flame className="w-5 h-5 text-red-400" />
                    <div>
                      <p className="text-white text-sm font-medium">Burn Required</p>
                      <p className="text-white/50 text-xs">Must burn BURNZ to claim</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <p className="text-white/50 text-xs mb-2">Tier Conversion Rates:</p>
                  {Object.entries(CONVERSION_CONFIG.tierBonuses).map(([tier, bonus]) => (
                    <div key={tier} className={`flex justify-between text-xs py-1 ${tier === currentTier.id ? 'text-orange-400' : 'text-white/50'}`}>
                      <span className="capitalize">{tier}</span>
                      <span>{bonus}x rate, {(CONVERSION_CONFIG.tierBurnReduction[tier] * 100).toFixed(0)}% burn</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* XP is Non-Transferable Notice */}
          <Card className="bg-yellow-500/5 border-yellow-500/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-yellow-400 font-medium mb-1">Important: XP Properties</p>
                  <ul className="text-white/70 text-sm space-y-1">
                    <li>• XP is <span className="text-white font-medium">non-transferable</span> - bound to your account</li>
                    <li>• XP has <span className="text-white font-medium">slow decay</span> - {(LEVEL_CONFIG.xpDecayRate * 100).toFixed(0)}% per week after {LEVEL_CONFIG.xpDecayGracePeriod} days inactive</li>
                    <li>• Converted XP is <span className="text-white font-medium">destroyed permanently</span></li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Burn Tracker Tab */}
      {activeTab === "burns" && (
        <div className="space-y-6">
          {/* Burn Impact Banner */}
          <Card className="bg-gradient-to-r from-red-900/50 via-orange-900/30 to-yellow-900/20 border-red-500/30 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="bg-red-500/20 text-red-400 border-0 mb-2">🔥 DEFLATIONARY ECONOMY</Badge>
                  <h2 className="text-3xl font-bold text-white mb-2">Total BURNZ Destroyed</h2>
                  <p className="text-white/50">Making everyone's tokens more valuable</p>
                </div>
                <div className="text-right">
                  <p className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400">
                    {DEMO_BURN_STATS.totalBurned.toLocaleString()}
                  </p>
                  <p className="text-white/50 text-sm mt-1">{DEMO_BURN_STATS.burnRate}% of circulating supply</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Burn Stats Grid */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-[#1A1A1A] border-red-500/20">
              <CardContent className="p-4 text-center">
                <Flame className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-400">{DEMO_BURN_STATS.burnedToday.toLocaleString()}</p>
                <p className="text-white/50 text-xs">Burned Today</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1A1A1A] border-orange-500/20">
              <CardContent className="p-4 text-center">
                <Flame className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-orange-400">{DEMO_BURN_STATS.burnedThisWeek.toLocaleString()}</p>
                <p className="text-white/50 text-xs">Burned This Week</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1A1A1A] border-yellow-500/20">
              <CardContent className="p-4 text-center">
                <Flame className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-yellow-400">{DEMO_BURN_STATS.burnedThisMonth.toLocaleString()}</p>
                <p className="text-white/50 text-xs">Burned This Month</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1A1A1A] border-purple-500/20">
              <CardContent className="p-4 text-center">
                <Percent className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-400">{DEMO_BURN_STATS.burnRate}%</p>
                <p className="text-white/50 text-xs">Supply Reduced</p>
              </CardContent>
            </Card>
          </div>

          {/* Your Burn Impact */}
          <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-400" />
                Your Burn Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-orange-400">{DEMO_BURN_STATS.userBurned.toLocaleString()}</p>
                  <p className="text-white/50 text-sm">Total BURNZ You've Burned</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-purple-400">#{DEMO_BURN_STATS.userBurnRank}</p>
                  <p className="text-white/50 text-sm">Your Burn Rank</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-green-400">{DEMO_BURN_STATS.supplyReduction}%</p>
                  <p className="text-white/50 text-sm">Your Supply Impact</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-white/5 rounded-lg text-center">
                <p className="text-white/70">🔥 You've helped reduce total supply by <span className="text-orange-400 font-bold">{DEMO_BURN_STATS.supplyReduction}%</span>!</p>
              </div>
            </CardContent>
          </Card>

          {/* Burn Leaderboard */}
          <Card className="bg-[#1A1A1A] border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Top Burners This Month 🔥</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {DEMO_BURN_STATS.topBurners.map((burner, i) => (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${i === 0 ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20' : 'bg-white/5'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${i === 0 ? 'bg-yellow-500 text-black' : i === 1 ? 'bg-slate-400 text-black' : 'bg-orange-600 text-white'}`}>
                        {burner.rank}
                      </div>
                      <span className="text-white font-medium">{burner.username}</span>
                    </div>
                    <span className="text-red-400 font-bold">{burner.burned.toLocaleString()} 🔥</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Burn Events */}
          <Card className="bg-[#1A1A1A] border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Burn Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(BURN_EVENTS).filter(([_, e]) => e.visible).map(([key, event]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-white/70 text-sm">{event.description}</span>
                    <Badge className="bg-red-500/20 text-red-400 border-0">{(event.rate * 100).toFixed(0)}% burn</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Progression Tab */}
      {activeTab === "progression" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Prestige Tiers</h2>
              <p className="text-white/50">Level up for XP bonuses and better conversion efficiency</p>
            </div>
            <Badge className="bg-purple-500/20 text-purple-400 border-0">
              High tiers = Efficiency, not more emissions
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            {PRESTIGE_TIERS.map((tier) => {
              const isCurrentTier = tier.id === currentTier.id;
              const isUnlocked = userData.level >= tier.minLevel;
              
              return (
                <Card key={tier.id} className={`border transition-all ${isCurrentTier ? 'border-orange-500/50 bg-orange-500/10' : isUnlocked ? 'border-white/10 bg-[#1A1A1A]' : 'border-white/5 bg-[#0D0D0D] opacity-60'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tier.gradient} flex items-center justify-center text-3xl shadow-lg ${!isUnlocked ? 'grayscale' : ''}`}>
                        {isUnlocked ? tier.icon : <Lock className="w-6 h-6 text-white/50" />}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-bold text-lg ${isUnlocked ? 'text-white' : 'text-white/50'}`}>{tier.name}</h3>
                          {isCurrentTier && <Badge className="bg-green-500 text-white border-0">CURRENT</Badge>}
                          {tier.canConvertXP && !isUnlocked && <Badge className="bg-cyan-500/20 text-cyan-400 border-0">Unlocks Conversion</Badge>}
                        </div>
                        <p className="text-white/50 text-sm">Level {tier.minLevel} - {tier.maxLevel}</p>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-1">
                          {tier.benefits.map((benefit, i) => (
                            <Badge key={i} className={`text-[10px] border-0 ${isUnlocked ? 'bg-white/10 text-white/70' : 'bg-white/5 text-white/30'}`}>
                              {benefit}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-white/30 text-xs">XP Bonus</p>
                          <p className={`font-bold ${isUnlocked ? 'text-green-400' : 'text-white/30'}`}>+{(tier.xpBonus * 100).toFixed(0)}%</p>
                        </div>
                        <div>
                          <p className="text-white/30 text-xs">Convert Rate</p>
                          <p className={`font-bold ${isUnlocked && tier.canConvertXP ? 'text-orange-400' : 'text-white/30'}`}>
                            {tier.canConvertXP ? `${CONVERSION_CONFIG.tierBonuses[tier.id]}x` : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-white/30 text-xs">Vote Power</p>
                          <p className={`font-bold ${isUnlocked ? 'text-purple-400' : 'text-white/30'}`}>{tier.votingPower}x</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Badges Tab */}
      {activeTab === "badges" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Badge Collection</h2>
              <p className="text-white/50">{userData.earnedBadges.length} / {BADGES.length} badges earned</p>
            </div>
          </div>
          
          {Object.entries(BADGE_CATEGORIES).map(([catId, category]) => {
            const categoryBadges = BADGES.filter(b => b.category === catId);
            if (categoryBadges.length === 0) return null;
            
            return (
              <div key={catId} className="space-y-3">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <span className="text-xl">{category.icon}</span>
                  {category.name}
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  {categoryBadges.map((badge) => {
                    const isEarned = userData.earnedBadges.includes(badge.id);
                    const rarityInfo = BADGE_RARITY[badge.rarity];
                    
                    return (
                      <Card key={badge.id} className={`border transition-all hover:scale-105 ${isEarned ? `border-${rarityInfo.color}-500/30 ${rarityInfo.glow}` : 'border-white/5 opacity-50'}`}>
                        <CardContent className="p-4 text-center">
                          <div className={`text-4xl mb-2 ${!isEarned ? 'grayscale' : ''}`}>
                            {isEarned ? badge.icon : '🔒'}
                          </div>
                          <h4 className={`font-bold text-sm ${isEarned ? 'text-white' : 'text-white/50'}`}>{badge.name}</h4>
                          <Badge className={`mt-1 text-[9px] border-0 bg-${rarityInfo.color}-500/20 text-${rarityInfo.color}-400`}>
                            {rarityInfo.name}
                          </Badge>
                          <p className="text-white/30 text-[10px] mt-2 line-clamp-2">{badge.description}</p>
                          {isEarned && (
                            <p className="text-green-400 text-xs mt-1">+{badge.xp_bonus} XP bonus</p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Seasonal Tab */}
      {activeTab === "seasonal" && (
        <div className="space-y-6">
          {/* Season Banner */}
          <Card className="bg-gradient-to-br from-cyan-900/50 via-[#1A1A1A] to-blue-900/30 border-cyan-500/20 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="bg-cyan-500/20 text-cyan-400 border-0 mb-2">{CURRENT_SEASON.icon} ACTIVE SEASON</Badge>
                  <h2 className="text-3xl font-bold text-white mb-2">{CURRENT_SEASON.name}</h2>
                  <p className="text-white/50 max-w-lg">{CURRENT_SEASON.description}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <Clock className="w-4 h-4 text-cyan-400" />
                    <span className="text-white/50 text-sm">Ends: {new Date(CURRENT_SEASON.endDate).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-white/50 text-sm mb-1">Your Season XP</p>
                  <p className="text-4xl font-bold text-cyan-400">{userData.seasonalXP.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Season Rewards Track */}
          <Card className="bg-[#1A1A1A] border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Season Rewards Track</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="absolute top-6 left-0 right-0 h-2 bg-white/10 rounded-full">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                    style={{ width: `${Math.min((userData.seasonalXP / 30000) * 100, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between pt-0">
                  {CURRENT_SEASON.rewards.map((reward, i) => {
                    const isUnlocked = userData.seasonalXP >= reward.threshold;
                    return (
                      <div key={i} className="text-center relative">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl border-4 ${isUnlocked ? 'bg-cyan-500 border-cyan-400' : 'bg-[#1A1A1A] border-white/20'}`}>
                          {isUnlocked ? <Check className="w-6 h-6 text-white" /> : reward.icon}
                        </div>
                        <p className="text-white/50 text-xs mt-2">{reward.threshold.toLocaleString()} XP</p>
                        <p className={`text-xs font-medium mt-1 ${isUnlocked ? 'text-cyan-400' : 'text-white/30'}`}>{reward.reward}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Season Challenges */}
          <Card className="bg-[#1A1A1A] border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Season Challenges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {CURRENT_SEASON.challenges.map((challenge) => (
                  <div key={challenge.id} className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-bold">{challenge.name}</h4>
                      <span className="text-orange-400 text-sm">+{challenge.xp} XP</span>
                    </div>
                    <p className="text-white/50 text-sm mb-3">{challenge.description}</p>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white/50">Progress</span>
                        <span className="text-white">{challenge.progress}/{challenge.target}</span>
                      </div>
                      <Progress value={(challenge.progress / challenge.target) * 100} className="h-2" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Hard-Capped Leaderboard Prizes */}
          <Card className="bg-[#1A1A1A] border-white/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Season Leaderboard Prizes</CardTitle>
                <Badge className="bg-red-500/20 text-red-400 border-0">BURNZ Capped</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {CURRENT_SEASON.leaderboardPrizes.slice(0, 3).map((prize, i) => (
                  <div key={i} className={`p-4 rounded-xl text-center ${i === 0 ? 'bg-yellow-500/10 border border-yellow-500/30' : i === 1 ? 'bg-slate-500/10 border border-slate-500/30' : 'bg-orange-500/10 border border-orange-500/30'}`}>
                    <span className="text-3xl">{prize.icon}</span>
                    <p className="text-white font-bold mt-2">{prize.rank}</p>
                    <p className="text-orange-400 text-sm mt-1">+{prize.xp.toLocaleString()} XP</p>
                    <p className="text-purple-400 text-sm">+{prize.burnz} BURNZ</p>
                    {prize.badge && <Badge className="mt-2 bg-white/10 text-white/70 text-xs border-0">{prize.badge}</Badge>}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                {CURRENT_SEASON.leaderboardPrizes.slice(3).map((prize, i) => (
                  <div key={i} className="p-3 bg-white/5 rounded-xl text-center">
                    <span className="text-2xl">{prize.icon}</span>
                    <p className="text-white/70 text-sm mt-1">{prize.rank}</p>
                    <p className="text-orange-400 text-xs">+{prize.xp} XP {prize.burnz > 0 && `+ ${prize.burnz} BURNZ`}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === "leaderboard" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Global Leaderboard</h2>
            <div className="flex items-center gap-2">
              <Badge className="bg-purple-500/20 text-purple-400 border-0">Weekly</Badge>
              <Badge className="bg-white/10 text-white/50 border-0">Monthly</Badge>
              <Badge className="bg-white/10 text-white/50 border-0">All Time</Badge>
            </div>
          </div>

          {/* Your Rank Card */}
          <Card className="bg-gradient-to-r from-purple-500/20 to-orange-500/20 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold text-white">#{userData.rank}</div>
                  <div>
                    <p className="text-white font-bold">Your Position</p>
                    <p className="text-white/50 text-sm">{userData.totalXP.toLocaleString()} XP this week</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ArrowUp className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-bold">+8 positions</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Leaderboard Table */}
          <Card className="bg-[#1A1A1A] border-white/5">
            <CardContent className="p-0">
              {DEMO_LEADERBOARD.map((player, i) => {
                const tier = PRESTIGE_TIERS.find(t => t.id === player.tier) || PRESTIGE_TIERS[0];
                return (
                  <div key={player.rank} className={`flex items-center justify-between p-4 border-b border-white/5 last:border-0 ${i < 3 ? 'bg-gradient-to-r from-transparent via-white/5 to-transparent' : ''}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${i === 0 ? 'bg-yellow-500 text-black' : i === 1 ? 'bg-slate-400 text-black' : i === 2 ? 'bg-orange-600 text-white' : 'bg-white/10 text-white'}`}>
                        {player.rank}
                      </div>
                      <img src={player.avatar} alt={player.username} className="w-12 h-12 rounded-xl" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-bold">{player.username}</p>
                          <span className="text-lg">{tier.icon}</span>
                        </div>
                        <p className="text-white/50 text-sm">Level {player.level} • {player.badges} badges</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-orange-400 font-bold">{player.xp.toLocaleString()} XP</p>
                      <p className="text-red-400 text-xs">{player.burnzBurned?.toLocaleString() || 0} burned 🔥</p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      )}

      {/* How to Earn Tab - Updated for XP-First */}
      {activeTab === "earning" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">How to Earn XP & BURNZ</h2>
              <p className="text-white/50">90% XP rewards, BURNZ through milestones & conversion</p>
            </div>
            <Badge className="bg-orange-500/20 text-orange-400 border-0">XP-First Economy</Badge>
          </div>

          {/* Economy Explanation */}
          <Card className="bg-gradient-to-r from-purple-500/10 to-orange-500/10 border-purple-500/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-purple-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium mb-2">Why XP-First?</p>
                  <p className="text-white/70 text-sm">
                    BURNZ is scarce and strategic. You earn abundant XP through actions, then choose when to convert it to BURNZ. 
                    Conversion requires burning BURNZ, which makes everyone's tokens more valuable. High-tier users get better conversion rates, 
                    not more free tokens.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-2 gap-6">
            {/* XP Earning Actions */}
            <Card className="bg-[#1A1A1A] border-green-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-green-400 text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  XP Earning Actions (90%)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(EARNING_RULES)
                    .filter(([_, rule]) => rule.xp > 0)
                    .slice(0, 10)
                    .map(([ruleId, rule]) => (
                      <div key={ruleId} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                        <span className="text-white/70 text-sm">{rule.description}</span>
                        <span className="text-green-400 font-bold text-sm">+{rule.xp} XP</span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            {/* BURNZ Milestones */}
            <Card className="bg-[#1A1A1A] border-orange-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-orange-400 text-lg flex items-center gap-2">
                  <Flame className="w-5 h-5" />
                  Direct BURNZ (Milestones Only)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(EARNING_RULES)
                    .filter(([_, rule]) => rule.burnz > 0)
                    .map(([ruleId, rule]) => (
                      <div key={ruleId} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                        <span className="text-white/70 text-sm">{rule.description}</span>
                        <span className="text-orange-400 font-bold text-sm">+{rule.burnz} BURNZ</span>
                      </div>
                    ))}
                </div>
                <div className="mt-4 p-3 bg-orange-500/10 rounded-lg">
                  <p className="text-orange-300 text-xs">
                    💡 Most BURNZ comes from converting XP. Level up to Silver (25) to unlock conversion!
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Dump Zone Safeguards */}
          <Card className="bg-[#1A1A1A] border-yellow-500/20">
            <CardHeader>
              <CardTitle className="text-yellow-400 text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Dump Zone Safeguards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-white/50 text-sm mb-4">NFT dumping is for recovery & closure, not farming. These safeguards prevent abuse:</p>
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 bg-white/5 rounded-lg text-center">
                  <p className="text-white font-bold">{DUMP_SAFEGUARDS.maxDumpsPerDay}</p>
                  <p className="text-white/50 text-xs">Max dumps/day</p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg text-center">
                  <p className="text-white font-bold">{DUMP_SAFEGUARDS.weeklyDumpCap}</p>
                  <p className="text-white/50 text-xs">Max dumps/week</p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg text-center">
                  <p className="text-white font-bold">{DUMP_SAFEGUARDS.nftMinAgeHours}h</p>
                  <p className="text-white/50 text-xs">Min hold time</p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg text-center">
                  <p className="text-white font-bold">↓</p>
                  <p className="text-white/50 text-xs">Diminishing returns</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pro Tips */}
          <Card className="bg-gradient-to-r from-green-500/10 to-cyan-500/10 border-green-500/20">
            <CardContent className="p-6">
              <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-green-400" />
                Pro Tips for the New Economy
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-green-400 font-bold mb-1">🎯 Focus on XP</p>
                  <p className="text-white/50 text-sm">Don't chase BURNZ directly. Stack XP, then convert strategically.</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-green-400 font-bold mb-1">⚡ Level to Silver</p>
                  <p className="text-white/50 text-sm">Level 25 unlocks conversion. Higher tiers = better rates.</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-green-400 font-bold mb-1">🔥 Embrace Burns</p>
                  <p className="text-white/50 text-sm">Every burn reduces supply. Your tokens become more valuable.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RewardsPage;
